import { httpServiceClient } from '@/infra/http'
import { NamingStrategy } from '@/infra/http/http-service-client/http-service-client-types'
import { makeApiUrl } from '@/main/factories/http/api-url-factory'
import { Pagination } from '@/domain/models'
import {
  ProductValuesParams,
  ProductCityValuesParams,
  ProductUnitValuesParams,
  ProductStatesResponse,
  ProductCitiesResponse,
  ProductAvailableOptionsRequest,
  ProductAvailableOptionResponse,
  ProductExtraInfoResponse,
  ProductExtraInfoQuery,
  ClassTypeRequest,
  ClassTypeResponse,
  ProductAvailableInfoRequest,
  ProductAvailableInfoRawResponse,
  ProductUnitsResponseRaw
} from './product-content-service-types'
import {
  formatProductStates,
  formatProductCities,
  formatProductUnits,
  transformProductAvailableResponse,
  formatProductAvailableInfo
} from './product-content-service-utils'

/**
 * Camada de serviço responsável pelos endpoints do catálogo de produtos.
 *
 * Todos os endpoints seguem o padrão base: `/catalog/v2/products/{productId}/*`
 *
 * Criada como factory (`createProductContentService`) para permitir injeção de
 * um client customizado em testes, sem precisar mockar o módulo inteiro.
 * Em produção, a instância padrão `productContentService` usa o `httpServiceClient` global.
 */

// Monta a URL base de um produto específico — reutilizada por todos os métodos do service
const getProductUrl = (productId: number) => makeApiUrl(`/catalog/v2/products/${productId}`)

export const createProductContentService = (client = httpServiceClient) => ({
  /**
   * Busca as ofertas (turmas) disponíveis para o produto, filtradas por unidade e tipo de turma.
   * Endpoint: GET /catalog/v2/products/{productId}/available-options
   *
   * A resposta bruta passa por `transformProductAvailableResponse` que:
   * - Formata datas e horários para exibição
   * - Traduz turno e duração de aula
   * - Resolve o plano exigido a partir do requiredPlanId
   */
  async fetchAvailableOptions({ productId, ...params }: ProductAvailableOptionsRequest) {
    const response = await client<ProductAvailableOptionResponse>({
      method: 'get',
      url: `${getProductUrl(productId)}/available-options`,
      params: { ...params, page: params.page ?? Pagination.DEFAULT_PAGE }
    })

    return transformProductAvailableResponse(response.body.data as ProductAvailableOptionResponse)
  },

  /**
   * Busca os estados onde o produto tem unidades físicas disponíveis.
   * Endpoint: GET /catalog/v2/products/{productId}/states
   *
   * `isDependentContext` é enviado em snake_case via `paramsNamingStrategy`.
   * Retorna array de strings com os nomes dos estados (ex: ["SP", "RJ", "MG"]).
   */
  async fetchStates({ productId, isDependentContext }: ProductValuesParams) {
    // Só inclui o param quando explicitamente definido — undefined omite do request
    const params = isDependentContext !== undefined ? { isDependentContext } : undefined

    const response = await client<ProductStatesResponse>({
      method: 'get',
      url: `${getProductUrl(productId)}/states`,
      paramsNamingStrategy: NamingStrategy.snakeCase,
      params
    })

    return formatProductStates(response.body.data as ProductStatesResponse)
  },

  /**
   * Busca as cidades do estado informado onde o produto tem unidades.
   * Endpoint: GET /catalog/v2/products/{productId}/cities?state={state}
   *
   * Retorna array de strings com os nomes das cidades (ex: ["São Paulo", "Campinas"]).
   */
  async fetchCities({ productId, state, isDependentContext }: ProductCityValuesParams) {
    const params = {
      state,
      ...(isDependentContext !== undefined && { isDependentContext })
    }

    const response = await client<ProductCitiesResponse>({
      method: 'get',
      url: `${getProductUrl(productId)}/cities`,
      paramsNamingStrategy: NamingStrategy.snakeCase,
      params
    })

    return formatProductCities(response.body.data as ProductCitiesResponse)
  },

  /**
   * Busca as unidades físicas disponíveis na cidade informada para o produto.
   * Endpoint: GET /catalog/v2/products/{productId}/units?state={state}&city={city}
   *
   * A resposta bruta passa por `formatProductUnits` que:
   * - Achata o objeto `address` em uma string única de exibição
   * - Extrai state, city e name para campos de primeiro nível
   */
  async fetchUnits(params: ProductUnitValuesParams) {
    const { productId, state, city, isDependentContext, page, pageSize, ...restParams } = params

    const queryParams = {
      state,
      city,
      page: String(page ?? Pagination.DEFAULT_PAGE),
      pageSize: String(pageSize ?? 10),
      ...(isDependentContext !== undefined && { isDependentContext }),
      ...restParams
    }

    const response = await client<ProductUnitsResponseRaw>({
      method: 'get',
      url: `${getProductUrl(productId)}/units`,
      paramsNamingStrategy: NamingStrategy.snakeCase,
      params: queryParams
    })

    return formatProductUnits(response.body.data as ProductUnitsResponseRaw)
  },

  /**
   * Busca informações extras da oferta selecionada (carga horária, prazo de matrícula, etc).
   * Endpoint: GET /catalog/v2/products/{productId}/extra-info?offerId={offerId}
   *
   * Dois modos de uso:
   * - Com `offerId`: retorna dados da oferta específica
   * - Sem `offerId`: servidor faz um double-check e pode retornar dados do produto
   *   (usado quando a lista de ofertas retorna vazia — o `id` retornado vira o offerId)
   */
  async fetchExtraInfo({ productId, offerId }: ProductExtraInfoQuery) {
    // offerId é opcional — quando ausente o servidor usa o productId para resolver
    const params = offerId !== undefined ? { offerId } : undefined

    const response = await client<ProductExtraInfoResponse>({
      method: 'get',
      url: `${getProductUrl(productId)}/extra-info`,
      paramsNamingStrategy: NamingStrategy.snakeCase,
      params
    })

    return response.body.data as ProductExtraInfoResponse
  },

  /**
   * Busca os tipos de turma disponíveis para o produto (ex: Individual, Em grupo).
   * Endpoint: GET /catalog/v2/products/{productId}/class-types
   *
   * Quando o produto tem unidades, `unitName` é passado para filtrar os tipos
   * disponíveis especificamente naquela unidade.
   */
  async fetchRequestClassType(productId: number, params: ClassTypeRequest) {
    const response = await client<ClassTypeResponse[]>({
      method: 'get',
      url: `${getProductUrl(productId)}/class-types`,
      params: { ...params }
    })

    return response.body.data as ClassTypeResponse[]
  },

  /**
   * Busca a cobertura de plano do produto para o usuário atual.
   * Endpoint: GET /catalog/v2/products/{productId}/available-info
   *
   * A resposta bruta passa por `formatProductAvailableInfo` que resolve
   * os IDs numéricos de planos para objetos `Plan.Enum` com nome, cor, etc.
   *
   * Executado no data loader da rota para estar disponível imediatamente na página.
   */
  async fetchAvailableInfo({ productId, isDependentContext }: ProductAvailableInfoRequest) {
    const params = isDependentContext !== undefined ? { isDependentContext } : undefined

    const response = await client<ProductAvailableInfoRawResponse>({
      method: 'get',
      url: `${getProductUrl(productId)}/available-info`,
      paramsNamingStrategy: NamingStrategy.snakeCase,
      params
    })

    return formatProductAvailableInfo(response.body.data as ProductAvailableInfoRawResponse)
  }
})

// Instância singleton para uso em toda a aplicação
export const productContentService = createProductContentService()
